import { Controller } from "@hotwired/stimulus";

// Uploads the simple volumes template, prefills facility+year rows, and applies a unit to all rows.
export default class extends Controller {
  static targets = ["input", "status", "importButton", "bulkUnit"];

  static values = {
    previewUrl: String,
    buyerId: String,
    invalidTypeMessage: String,
    genericErrorMessage: String,
    yearsMismatchMessage: String,
  };

  browse() {
    this.inputTarget.click();
  }

  apply(event) {
    event.preventDefault();

    const bulk = this.hasBulkUnitTarget
      ? this.bulkUnitTarget
      : document.getElementById("volume_unit_bulk");
    const value = bulk?.value;
    if (!value) return;

    document.querySelectorAll("[data-invite-volume-unit]").forEach((select) => {
      select.value = String(value);
      select.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  onFileChange() {
    void this._importFile();
  }

  async _importFile() {
    const file = this.inputTarget.files?.[0];
    if (!file || !this.previewUrlValue) return;

    if (!this._accepts(file)) {
      this._setStatus(
        this.invalidTypeMessageValue || "Invalid file type. Please upload a .xlsx file.",
        true
      );
      this.inputTarget.value = "";
      return;
    }

    this._setBusy(true);
    this._setStatus("");

    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    const body = new FormData();
    body.append("file", file);
    if (this.buyerIdValue) body.append("invite[buyer_id]", this.buyerIdValue);

    try {
      const res = await fetch(this.previewUrlValue, {
        method: "POST",
        body,
        headers: {
          Accept: "application/json",
          ...(token ? { "X-CSRF-Token": token } : {}),
        },
        credentials: "same-origin",
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        this._setStatus(
          data.error ||
            this.genericErrorMessageValue ||
            "Could not import volumes from this spreadsheet.",
          true
        );
        return;
      }

      const applied = this._applyMatchedRows(Array.isArray(data.matched_rows) ? data.matched_rows : []);
      const message =
        applied === 0 && (data.matched_rows || []).length > 0
          ? this.yearsMismatchMessageValue ||
            "Imported rows did not match any payroll years shown in the table."
          : data.message || "";
      this._setStatus(message, false);
    } catch (_error) {
      this._setStatus(
        this.genericErrorMessageValue || "Could not import volumes from this spreadsheet.",
        true
      );
    } finally {
      this._setBusy(false);
      this.inputTarget.value = "";
    }
  }

  _applyMatchedRows(rows) {
    let applied = 0;

    rows.forEach((row) => {
      const facilityId = String(row.facility_id || "");
      const year = String(row.year || "");
      if (!facilityId || !year) return;

      const amountInput = this._findField("inviteVolumeAmount", facilityId, year);
      if (!amountInput) return;

      amountInput.value = row.volume;
      amountInput.dispatchEvent(new Event("input", { bubbles: true }));
      applied += 1;

      if (row.unit_of_production_id) {
        const unitSelect = this._findField("inviteVolumeUnit", facilityId, year);
        if (unitSelect) {
          unitSelect.value = String(row.unit_of_production_id);
          unitSelect.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }
    });

    return applied;
  }

  // Match by data attributes so facility ids with "#" / special chars still resolve.
  _findField(markerKey, facilityId, year) {
    return Array.from(document.querySelectorAll(`[data-${this._datasetToAttr(markerKey)}]`)).find(
      (el) => el.dataset.facilityId === facilityId && String(el.dataset.payrollYear) === year
    );
  }

  _datasetToAttr(datasetKey) {
    return datasetKey.replace(/[A-Z]/g, (ch) => `-${ch.toLowerCase()}`);
  }

  _setBusy(busy) {
    if (!this.hasImportButtonTarget) return;
    this.importButtonTarget.disabled = busy;
    this.importButtonTarget.classList.toggle("opacity-70", busy);
    this.importButtonTarget.classList.toggle("cursor-not-allowed", busy);
  }

  _setStatus(message, isError = false) {
    if (!this.hasStatusTarget) return;
    this.statusTarget.textContent = message || "";
    this.statusTarget.classList.toggle("text-red-600", !!isError);
    this.statusTarget.classList.toggle("text-gray-modern-600", !isError && !!message);
  }

  _accepts(file) {
    const name = (file.name || "").toLowerCase();
    return name.endsWith(".xlsx");
  }
}
